import React, { useEffect, useState } from "react";
import { getPaymentHistory } from "../api/client.js";

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatCurrency(amount, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount || 0);
}

const STATUS_STYLES = {
  SUCCEEDED: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200",
  PROCESSING: "border-amber-400/40 bg-amber-500/10 text-amber-200",
  FAILED: "border-rose-400/40 bg-rose-500/10 text-rose-200",
  PENDING: "border-sky-400/40 bg-sky-500/10 text-sky-200"
};

export default function CrmPaymentHistoryPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const load = () => {
    setLoading(true);
    setError("");
    getPaymentHistory({ page, pageSize })
      .then((response) => {
        setPayments(response?.items ?? []);
        setTotal(response?.total ?? 0);
      })
      .catch((err) => setError(err.message || "Unable to load payment history"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Financial records</p>
        <h1 className="text-3xl font-semibold text-white">Payment History</h1>
        <p className="text-sm text-white/70">
          All your payment transactions for HOME AI services.
        </p>
      </header>

      {error && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          Loading payment history…
        </div>
      ) : payments.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          No payment history found.
        </div>
      ) : (
        <div className="space-y-4">
          {payments.map((payment) => {
            const statusStyle = STATUS_STYLES[payment.status] || STATUS_STYLES.PENDING;
            return (
              <div
                key={payment.id}
                className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-wider ${statusStyle}`}
                      >
                        {payment.status}
                      </span>
                      {payment.onboardingStatus && (
                        <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] uppercase tracking-wider text-white/70">
                          {payment.onboardingStatus}
                        </span>
                      )}
                    </div>
                    <h2 className="text-lg font-semibold text-white">{payment.description}</h2>
                    <p className="text-xs text-white/60 mt-1">{payment.address}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-white">
                      {formatCurrency(payment.amount, payment.currency)}
                    </p>
                    {payment.paidAt && (
                      <p className="text-xs text-white/60 mt-1">
                        Paid: {formatDate(payment.paidAt)}
                      </p>
                    )}
                  </div>
                </div>

                {(payment.valuationAmount || payment.upfrontFee || payment.successFeePercent) && (
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-xs text-white/70 space-y-1">
                    {payment.valuationAmount && (
                      <p>
                        <span className="text-white/50">Property valuation:</span>{" "}
                        {formatCurrency(payment.valuationAmount, payment.currency)}
                      </p>
                    )}
                    {payment.upfrontFee && (
                      <p>
                        <span className="text-white/50">Upfront fee:</span>{" "}
                        {formatCurrency(payment.upfrontFee, payment.currency)}
                      </p>
                    )}
                    {payment.successFeePercent && (
                      <p>
                        <span className="text-white/50">Success fee:</span>{" "}
                        {payment.successFeePercent}% on completion
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-white/50">
                  <span>Payment ID: {payment.paymentIntentId || payment.id}</span>
                  <span>Created: {formatDate(payment.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs text-white">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="rounded-xl border border-white/15 bg-black/40 px-4 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {payments.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/60">
          <p className="font-semibold mb-2">Total payments: {total}</p>
          <p>
            Total amount:{" "}
            {formatCurrency(
              payments.reduce((sum, p) => sum + (p.amount || 0), 0),
              "GBP"
            )}
          </p>
        </div>
      )}
    </div>
  );
}
